import React from 'react';
import { withStyles } from '@material-ui/core';
import {
    Cancel as CancelIcon, CheckCircle, ViewList, LibraryBooks
} from '@markinson/uicomponents-v2/SvgIcons/';
import FormView from 'components/FormView';
import PickSlipLineDetailsForm from './LineDetails.from';
import styles from './LineDetails.styles';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import SummaryTable from 'components/common/SummaryTable';
import SummaryTableSchema from './LineDetails.summaryTable.schema';
import { SummaryTableSchema as KitSummaryableSchema } from '../KitComponents/KitComponents.summaryTable.schema';
import ILineDetailsProperties, { ILineDetailsHandle } from './LineDetails.properties';
import { useFetchCreditNoteLineDetails, useUpdateCreditNoteLineDetails, useCalculateCreditNoteLineDetails, useLoadKitComponents, useFetchCancelLineDetails } from 'api/creditNotes/creditNotes';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { CreditNoteLineDetailsCalculationFields } from '../CreditNotes.constants';
import { Route } from 'react-router';
import KitComponents from '../KitComponents';
import { useCNContextSelector, useCNDispatch } from '../CreditNotes.context';
import { IKitComponentLinesHandle } from 'components/common/KitComponents/KitComponentsLines';
import { CreditNotesActionTypes } from '../CreditNotes.actions';
import { isScreenRequest } from 'api/utils';
import { DEFAULT_PAGE_SIZE } from 'components/common/DataGridDevEx/DataGrid.constants';
import KitComponentDetails from '../KitComponents/KitComponentDetails';
import { IKitComponentDetailsHandle } from 'components/common/KitComponents/KitComponentDetails/KitComponentDetails.properties';
import { IWIPComponentFacade } from 'api/swaggerTypes';
import AddLotDialog from 'components/common/AddLotDialog';
import { useRetrieveLineDetailsInitialBin } from 'api/productReceipts/productReceipts';
import AddSerialDialog from 'components/common/AddSerialDialog';
import { useCalculateSerialNumber } from 'api/serialNumber/serialNumber';
import BinLocationsDialog from 'components/common/BinLocationsDialog';
import { formatNumberByPattern, selectActiveInputText } from 'utils/helpers';

const LineDetails = (props: ILineDetailsProperties, ref: React.Ref<ILineDetailsHandle>) => {
    const { classes, changeSelectedTab, formValues, dirty, resetForm, destroyForm, onUpdateSuccess, navigateBack, match } = props;
    const contextDispatch = useCNDispatch();
    const { setActionBar } = React.useContext(ActionBarContext);
    const params = new URLSearchParams(location.search);
    const WorkCreditNoteLineId = Number(params.get('WorkCreditNoteLineId') ?? 0);
    const CreditNoteId = Number(params.get('CreditNote') ?? 0);
    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const isDirtyState = internalDirty || dirty;
    const componentLinesRef = React.useRef<IKitComponentLinesHandle>();
    const componentDetailRef = React.useRef<IKitComponentDetailsHandle>();
    const descriptionFieldRef = React.useRef<HTMLInputElement>();
    const quantityFieldRef = React.useRef<HTMLInputElement>();
    const [componentLinesDirty, setComponentLinesDirty] = React.useState<boolean>(false);
    const [selectedComponentLine, setSelectedComponentLine] = React.useState<IWIPComponentFacade>();
    const BomCode = formValues ? formValues.BomCode : 0;
    const ReturnQuantity = formValues ? formValues.ReturnQuantity : 0;
    const { mutateAsync: loadKitComponentsMutateAsync } = useLoadKitComponents();
    const [lotDialogOpen, setLotDialogOpen] = React.useState<boolean>(false);
    const [addSerialDialogOpen, setAddSerialDialogOpen] = React.useState(false);
    const [addBinLocationDialogOpen, setAddBinLocationDialogOpen] = React.useState(false);
    const [initialValues, setInitialValues] = React.useState<any>();
    const [valuesSchema, setValuesSchema] = React.useState<any>();
    const [lineSummary, setLineSummary] = React.useState<any>();
    const isInitialLoadCompleteRef = React.useRef<boolean>(false);
    const CreditNoteContext = useCNContextSelector<'CreditNotesContext'>((state) => state.CreditNotesContext);
    // Fetch line details
    const { isLoading: creditNoteLineDetailsLoading, mutateAsync: fetchCreditNoteLineDetailsMutation } = useFetchCreditNoteLineDetails();
    const [selectedBinLot, setSelectedBinLot] = React.useState(null);
    const { data: lineDetailsInitialBin, mutateAsync: fetchLineDetailsInitialBin } = useRetrieveLineDetailsInitialBin();
    const { mutateAsync: calculateSerialNumberMutateAsync } = useCalculateSerialNumber();

    const pendingOkRef = React.useRef(false);

    const lotSerialTracked = React.useMemo
        (
            () =>
                initialValues && initialValues?.LotSerialTracked && initialValues?.LotSerialTracked?.toLowerCase() !== 'n' ? initialValues?.LotSerialTracked : ''
            ,
            [initialValues]
        );
    // Update line details mutation
    const updateCreditNoteLineDetailsMutation = useUpdateCreditNoteLineDetails();

    // Calculate line details mutation
    const calculateCreditNoteLineDetailsMutation = useCalculateCreditNoteLineDetails();

    // Cancel line details mutation
    const cancelCreditNoteLineDetailsMutation = useFetchCancelLineDetails();

    const fetchCreditNoteLineDetails = async (notifyUnsavedChanges: boolean = true) => {
        const response = await fetchCreditNoteLineDetailsMutation(
            {
                WorkCreditNoteLineId,
                urlQueryParams: {
                    NotifyUnsavedChanges: notifyUnsavedChanges
                }
            });

        if (response && response?.Status && response?.CreditNoteLineDetail?.inlineObject) {
            setValuesSchema(response?.CreditNoteLineDetail?.schema);
            setInitialValues(response?.CreditNoteLineDetail?.inlineObject);
            setLineSummary(response?.CreditNoteLineDetail?.inlineObject?.LineSummary);
        }
    };
    const [initialbinloaded, setInitialBinLoaded] = React.useState(false);

    // Redirect if WorkCreditNoteLineId is missing
    React.useEffect(
        () => {
            const WorkCreditNoteLineIdParam = params.get('WorkCreditNoteLineId');
            if (WorkCreditNoteLineIdParam && Number(WorkCreditNoteLineIdParam) === 0) {
                changeSelectedTab('Lines');
            }
        },
        [params, changeSelectedTab]
    );

    React.useEffect(
        () => {
            if (!isDirtyState) {
                const fieldToFocus = initialValues?.EnableDescription ? descriptionFieldRef : quantityFieldRef;
                setTimeout(
                    () => {
                        fieldToFocus?.current?.focus();
                    },
                    0
                );
            }
        },

        [quantityFieldRef, initialValues, isDirtyState, initialValues?.EnableDescription]);

    React.useEffect(
        () => {
            // Reset initial load flag when WorkCreditNoteLineId changes
            isInitialLoadCompleteRef.current = false;
            if (WorkCreditNoteLineId) {
                fetchCreditNoteLineDetails();
            }
        },
        [WorkCreditNoteLineId]);

    // Redirect to Lines screen if refreshing on component-details route
    React.useEffect(
        () => {
            const currentPath = window.location.pathname;
            const isOnComponentDetailsRoute = currentPath.includes('/kit-components/component-details');

            if (isOnComponentDetailsRoute && !selectedComponentLine && initialValues) {
                // Redirect back to Lines screen
                changeSelectedTab('Lines', {
                    search: {
                        CreditNote: CreditNoteId
                    }
                });
            }
        },
        [selectedComponentLine, initialValues, changeSelectedTab, CreditNoteId]);

    React.useEffect(
        () => {
            if (initialValues?.ProductId && CreditNoteContext?.Warehouse && initialValues?.BinTrack && !initialbinloaded) {
                setInitialBinLoaded(true);
                fetchLineDetailsInitialBin?.({ ProductId: initialValues?.ProductId, Warehouse: CreditNoteContext?.Warehouse });
            }
        },
        [initialValues?.ProductId, CreditNoteContext?.Warehouse, initialValues?.BinTrack, initialbinloaded]);

    const resetAndNavigateBack = React.useCallback(
        () => {
            if (resetForm) resetForm();
            if (destroyForm) destroyForm();
            setInternalDirty(false);
            navigateBack();
        },
        [resetForm, destroyForm, changeSelectedTab]
    );

    // Calculate line details
    const calculateLineDetail = React.useCallback(
        async (name: string, value: any) => {
            try {
                if (!WorkCreditNoteLineId || !CreditNoteId || creditNoteLineDetailsLoading ||
                    initialValues?.[name] === value) {
                    return null;
                }

                const response = await calculateCreditNoteLineDetailsMutation.mutateAsync({
                    WorkCreditNoteLineId,
                    CreditNoteLineDetail: {
                        ...formValues,
                        BinLot: undefined,
                        [name]: value
                    },
                    LotSerialDetails: selectedBinLot ?? undefined,
                    urlQueryParams: {
                        ChangedField: name
                    }
                });

                if (response?.Status) {
                    setInternalDirty(true);
                    setInitialValues(response?.CreditNoteLineDetail?.inlineObject);
                    setValuesSchema(response?.CreditNoteLineDetail?.schema);
                    setLineSummary(response?.CreditNoteLineDetail?.inlineObject?.LineSummary);
                    selectActiveInputText();
                } else {
                    if (resetForm) resetForm();
                }

                return response;
            } catch (e) {
                return null;
            }
        },
        [formValues, calculateCreditNoteLineDetailsMutation, CreditNoteId, WorkCreditNoteLineId, creditNoteLineDetailsLoading, initialValues, resetForm, setInitialValues, setLineSummary, setValuesSchema]
    );

    const updateCreditNoteLineDetails = React.useCallback(
        async () => {
            if (!initialValues) return false;

            const { Status, ...filteredFormValues } = initialValues;
            const response = await updateCreditNoteLineDetailsMutation.mutateAsync({
                WorkCreditNoteLineId,
                CreditNoteLineDetail: { ...filteredFormValues, BinLot: undefined },
                LotSerialDetails: selectedBinLot ?? undefined,
            });

            if (response?.Status) {
                setInternalDirty(false);
            }

            return response?.Status;
        },
        [initialValues, updateCreditNoteLineDetailsMutation, CreditNoteId, WorkCreditNoteLineId, selectedBinLot]
    );

    const cancelLine = React.useCallback(
        async () => {
            const response = await cancelCreditNoteLineDetailsMutation.mutateAsync({ WorkCreditNoteLineId });

            return response;
        },
        [WorkCreditNoteLineId]
    );

    const onCancel = React.useCallback(
        async () => {
            cancelLine().then((response) => {
                if (response.Status) resetAndNavigateBack();
            });
        },
        [resetAndNavigateBack]
    );

    const ok = React.useCallback(
        async () => {
            (document.activeElement as HTMLElement)?.blur?.();

            if (calculateCreditNoteLineDetailsMutation.isLoading) {
                pendingOkRef.current = true;

                return;
            }

            if (await updateCreditNoteLineDetails()) {
                if (onUpdateSuccess) {
                    onUpdateSuccess();
                }
                resetAndNavigateBack();
            }
        },
        [
            calculateCreditNoteLineDetailsMutation.isLoading,
            updateCreditNoteLineDetails,
            onUpdateSuccess,
            resetAndNavigateBack
        ]
    );

    const handleLoadComponents = React.useCallback(
        async () => {
            const response = await loadKitComponentsMutateAsync({
                WorkCreditNoteLineId,
                BomCode: '',
                returnQuantity: initialValues?.Quantity,
                urlQueryParams: {
                    BatchPage: 1,
                    BatchSize: DEFAULT_PAGE_SIZE,
                    Sort: ''
                }
            });
            if (response?.Status && !isScreenRequest(response?.Forms) && changeSelectedTab) {
                changeSelectedTab(null, null, 'KitComponents');
            }
        },
        [BomCode, ReturnQuantity, initialValues]
    );

    const handleLocalSerialSelection = React.useCallback(
        (): void => {
            switch (lotSerialTracked) {
                case 'lf':
                    setLotDialogOpen(true);
                    break;
                case 'sf':
                    setAddSerialDialogOpen(true);
                    break;
                default:
                    setAddBinLocationDialogOpen(true);
            }
        },
        [lotSerialTracked, setLotDialogOpen]
    );

    const centerIcons = React.useMemo(
        () => [
            {
                label: 'Lot/Serial',
                Icon: LibraryBooks,
                action: 'LineDetailsPicking',
                iconStyle: { fill: undefined },
                disabled: !initialValues?.EnableLotSerial,
                onCallback: handleLocalSerialSelection
            },
            {
                label: 'Components',
                Icon: ViewList,
                action: 'KitComponents',
                iconStyle: { fill: undefined },
                disabled: !initialValues?.EnableComponents,
            }
        ],
        [initialValues?.EnableLotSerial, handleLocalSerialSelection]
    );

    const rightIcons = React.useMemo(
        () => [
            {
                label: 'Cancel',
                Icon: CancelIcon,
                iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                action: 'LineDetailsCancel',
                invokeMouseDownCallback: true,
            },
            {
                label: 'OK',
                Icon: CheckCircle,
                action: 'LineDetailsOk',
            },
        ],
        []
    );

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons,
                rightIcons
            });
        },
        [rightIcons, centerIcons, location]
    );

    React.useEffect(
        () => {
            if (!calculateCreditNoteLineDetailsMutation.isLoading && pendingOkRef.current) {
                pendingOkRef.current = false;

                (async () => {
                    if (await updateCreditNoteLineDetails()) {
                        if (onUpdateSuccess) {
                            onUpdateSuccess();
                        }
                        resetAndNavigateBack();
                    }
                })();
            }
        },
        [
            calculateCreditNoteLineDetailsMutation.isLoading,
            updateCreditNoteLineDetails,
            onUpdateSuccess,
            resetAndNavigateBack
        ]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (isDirtyState) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirtyState;
            },
            components(): void {
                handleLoadComponents();
            },
            reload(): void {
                if (WorkCreditNoteLineId) {
                    fetchCreditNoteLineDetails(false);
                }
                setActionBar({
                    leftIcons: [],
                    centerIcons,
                    rightIcons
                });
            },
            componentDetailsWarehouse(): void {
                if (componentDetailRef.current) {
                    componentDetailRef.current.warehouse();
                }
            },
            componentDetailsLotSerial(): void {
                if (componentDetailRef.current) {
                    componentDetailRef.current.lotSerial();
                }
            },
            componentDetailOnOk(): void {
                if (componentDetailRef.current) {
                    componentDetailRef.current.onOk();
                }
            },
            componentDetailOnCancel(callBack?: () => void): void {
                if (componentDetailRef.current) {
                    componentDetailRef.current.onCancel(callBack);
                }
            },
            LineDetailsOk: ok,
            LineDetailsCancel: onCancel,
            LineDetailsPicking: handleLocalSerialSelection
        }),
        [onCancel, cancelLine, ok, isDirtyState, centerIcons, rightIcons, WorkCreditNoteLineId]
    );

    // Use backend objects directly for summary table
    const selectedCreditNoteLine = React.useMemo(
        () => {
            if (!lineSummary || !initialValues) {
                return {};
            }

            // Save it in context for further use
            contextDispatch({
                [CreditNotesActionTypes.setCreditNoteLineDetailSummary]: {
                    ...initialValues,
                    ...lineSummary
                },
            });

            // Combine creditNoteLineDetail and lineSummary properties directly from backend
            return {
                ...initialValues,
                ...lineSummary
            };
        },
        [lineSummary, initialValues]);

    // Callback for RestockingCharge field to trigger calculation
    const onRestockingChargeValueChanged = React.useCallback(
        (value: any) => {
            if (calculateCreditNoteLineDetailsMutation.isLoading) return;
            const data = initialValues;
            const currentValue = parseFloat(value) || 0;
            const originalValue = parseFloat(data?.RestockingCharge) || 0;

            if (currentValue !== originalValue) {
                setInternalDirty(true);
                calculateLineDetail('RestockingCharge', value);
            }
        },
        [calculateLineDetail, initialValues, calculateCreditNoteLineDetailsMutation.isLoading]
    );

    // Callback for Reason lookup field to trigger calculation
    const onReasonSelectedItemChange = React.useCallback(
        (item: any) => {
            if (calculateCreditNoteLineDetailsMutation.isLoading) return;
            // Prevent callback from triggering during initial load
            if (!isInitialLoadCompleteRef.current) {
                isInitialLoadCompleteRef.current = true;

                return;
            }
            if (item) {
                const reasonValue = item?.Value || item?.Code || item;
                const data = initialValues;
                const currentValue = String(reasonValue || '');
                const originalValue = String(data?.Reason || '');

                if (currentValue !== originalValue) {
                    setInternalDirty(true);
                    calculateLineDetail('Reason', reasonValue);
                }
            }
        },
        [calculateLineDetail, initialValues, calculateCreditNoteLineDetailsMutation.isLoading]
    );

    // Trigger this handler when field gets blur
    const formViewOnBlur = React.useCallback(
        async (e: any): Promise<void> => {
            const fieldName = e?.target?.name;
            let fieldValue = e?.target?.value;

            if (calculateCreditNoteLineDetailsMutation.isLoading) return;

            if (fieldName) {
                if (!CreditNoteLineDetailsCalculationFields.includes(fieldName)) {
                    return;
                }

                const data = initialValues;

                // Handle numeric fields (Quantity, Price, Discount, RestockingCharge) vs non-numeric fields (Reason)
                const numericFields = ['Quantity', 'Price', 'Discount', 'RestockingCharge'];
                const isNumericField = numericFields.includes(fieldName);

                let isValueChanged: boolean;
                if (isNumericField) {
                    // For numeric fields, compare using parseFloat
                    fieldValue = formatNumberByPattern(e?.target?.value, valuesSchema[fieldName]?.Format);
                    const originalValue = formatNumberByPattern(valuesSchema[fieldName]?.Value, valuesSchema[fieldName]?.Format);
                    isValueChanged = fieldValue !== originalValue;
                } else {
                    // For non-numeric fields (like Reason), compare as strings
                    isValueChanged = String(fieldValue || '') !== String(data?.[fieldName] || '');
                }

                // Only check for invalid quantity if it's the Quantity field
                // const isInvalidQuantity = fieldName === 'Quantity' && (fieldValue === null || fieldValue === '');

                // if (isValueChanged && !isInvalidQuantity) {
                if (isValueChanged) {
                    setInternalDirty(true);
                    await calculateLineDetail(fieldName, fieldValue);
                }
            }
        },
        [initialValues, calculateLineDetail, calculateCreditNoteLineDetailsMutation.isLoading]
    );

    const applyLotDialog = (binLots) => {
        calculateLineDetailPutAway(binLots);
    };

    const calculateLineDetailPutAway = async (binLots) => {

        const response = await calculateCreditNoteLineDetailsMutation.mutateAsync({
            WorkCreditNoteLineId,
            CreditNoteLineDetail: {
                ...formValues,
                //BinLot: binLots // need to discuss
            },
            LotSerialDetails: binLots ?? undefined,
            urlQueryParams: {
                ChangedField: 'LotSerial' // need to discuss
            }
        });

        if (response?.Status) {
            setInternalDirty(true);
            setInitialValues(response?.CreditNoteLineDetail?.inlineObject);
            setValuesSchema(response?.CreditNoteLineDetail?.schema);
            setLineSummary(response?.CreditNoteLineDetail?.inlineObject?.LineSummary);
            setSelectedBinLot(response?.CreditNoteLineDetail?.inlineObject?.BinLot);
            setLotDialogOpen(false);
            setAddSerialDialogOpen(false);
            setAddBinLocationDialogOpen(false);
        } else {
            resetForm();
        }

        return response;
    };

    return (
        <React.Fragment>
            <Route
                key={'KitComponents'}
                path={`${match?.path ?? ''}/kit-components`}
                exact
                render={() => (
                    <KitComponents
                        module='CreditNotes'
                        kitSummarySchema={KitSummaryableSchema}
                        forwardedRef={componentLinesRef}
                        worksaleLineId={WorkCreditNoteLineId}
                        warehouseEntity={''}
                        DocumentId={CreditNoteId}
                        DocumentType={'CN'}
                        navigateBack={() => {
                            changeSelectedTab('LineDetails', {
                                search: {
                                    CreditNote: CreditNoteId,
                                    LineNumber: initialValues?.LineNumber ?? Number(params.get('LineNumber')) ?? '',
                                    WorkCreditNoteLineId: WorkCreditNoteLineId
                                }
                            });
                            setActionBar({
                                leftIcons: [],
                                centerIcons,
                                rightIcons
                            });
                        }}
                        showDetailOnClick={(data) => {
                            setSelectedComponentLine(data);
                            changeSelectedTab(null, null, 'ComponentDetails');
                        }}
                        componentLinesDirty={componentLinesDirty}
                        setComponentLinesDirty={setComponentLinesDirty}
                    />
                )} />
            <Route
                key={'ComponentDetails'}
                path={`${match?.path ?? ''}/component-details`}
                exact
                render={() => (
                    <KitComponentDetails
                        ref={componentDetailRef}
                        worksaleId={CreditNoteId}
                        DocumentType={'CN'}
                        componentId={selectedComponentLine && selectedComponentLine.ComponentId}
                        LineNumber={selectedComponentLine && selectedComponentLine.LineNumber}
                        worksaleLineId={WorkCreditNoteLineId}
                        disableSupplied={ReturnQuantity === 0}
                        navigateBack={() => changeSelectedTab('KitComponents')}
                        setComponentLinesDirty={setInternalDirty}
                    />
                )} />
            {lotDialogOpen && <AddLotDialog
                open={lotDialogOpen}
                title={'Enter Lot Numbers'}
                DocumentType={'CN'}
                isBinTracked={initialValues?.BinTrack}
                LineNumber={initialValues?.LineNumber}
                WarehouseEntity={CreditNoteContext?.Warehouse}
                ProductId={Number(initialValues?.ProductId)}
                RequiredQuantity={Number(formValues?.Quantity)}
                defaultBin={lineDetailsInitialBin?.Bin?.inlineObject?.Bin}
                BinLots={selectedBinLot || initialValues?.BinLot}
                onApply={async (binLots) => {
                    applyLotDialog(binLots);
                }}
                onCancel={async () => { setLotDialogOpen(false); }}
                config={{ bypassValidation: true }}
            />}
            {addSerialDialogOpen && <AddSerialDialog
                open={addSerialDialogOpen}
                RequiredQuantity={Number(formValues?.Quantity)}
                BinLots={selectedBinLot || initialValues?.BinLot}
                isBinTracked={initialValues?.BinTrack}
                ProductId={Number(initialValues?.ProductId)}
                WarehouseEntity={CreditNoteContext?.Warehouse}
                defaultBin={lineDetailsInitialBin?.Bin?.inlineObject?.Bin}
                onApply={async (binLots) => {
                    applyLotDialog(binLots);
                }}
                onCancel={async () => { setAddSerialDialogOpen(false); }}
                calculateSerialNumber={calculateSerialNumberMutateAsync}
                DocumentType={'CN'}
            />}
            {addBinLocationDialogOpen && <BinLocationsDialog
                open={addBinLocationDialogOpen}
                LineNumber={initialValues?.LineNumber}
                WarehouseEntity={CreditNoteContext?.Warehouse}
                ProductId={Number(initialValues?.ProductId)}
                RequiredQuantity={Number(formValues?.Quantity)}
                BinLots={selectedBinLot || initialValues?.BinLot}
                DocumentType={'CN'}
                onApply={async (binLots) => {
                    applyLotDialog(binLots);
                }}
                onCancel={async () => { setAddBinLocationDialogOpen(false); }}
            />}
            <div className={classes.formContainer}>
                <FormView
                    isLoading={creditNoteLineDetailsLoading}
                    formName={'CreditNotesLineDetailsForm'}
                    schema={PickSlipLineDetailsForm}
                    includeTabsHeight={false}
                    initialValues={initialValues}
                    valuesSchema={valuesSchema}
                    operationMode={'EDIT'}
                    onBlur={formViewOnBlur}
                    summaryTableRenderer={
                        (() => <SummaryTable
                            loading={creditNoteLineDetailsLoading}
                            selectedCreditNoteLine={selectedCreditNoteLine}
                            data={SummaryTableSchema}
                        />)}
                    fieldExtensions={
                        {
                            Quantity: {
                                fieldRef: (Ref) => quantityFieldRef.current = Ref,
                            },
                            Description: {
                                disabled: !initialValues?.EnableDescription,
                                fieldRef: (Ref) => descriptionFieldRef.current = Ref,
                            },
                            Price: {
                                disabled: !initialValues?.EnablePrice
                            },
                            Discount: {
                                disabled: !initialValues?.EnableDiscount
                            },
                            RestockingCharge: {
                                creditNoteId: CreditNoteId,
                                lineNumber: initialValues?.LineNumber || 0,
                                creditAmount: initialValues?.TotalCreditAmount,
                                disabled: !initialValues?.EnableRestockingCharge,
                                onValueChanged: onRestockingChargeValueChanged
                            },
                            Reason: {
                                disabled: !initialValues?.EnableCreditReason,
                                onSelectedItemChange: onReasonSelectedItemChange
                            }
                        }
                    }
                />
            </div>
        </React.Fragment>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(LineDetails)));
